import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import logoIcon from 'src/icons/logo.svg';
import loginIllustration from 'src/icons/login-illustration.svg';
import Input from 'src/components/input/Input';
import Button from 'src/components/button/Button';
import { isValidEmail, isValidPassword } from 'src/utils/forms';
import API from 'src/utils/api';
import { LOCAL_STORAGE_TOKEN_KEY } from 'src/utils/constants';
import Modal from 'src/components/modal/Modal';
import Loader from 'src/components/loader/Loader';

const Login = () => {
    const [email, setEmail] = useState();
    const [password, setPassword] = useState();
    const [errors, setErrors] = useState();
    const [loading, setLoading] = useState();
    // reset password
    const [resetPasswordEmail, setResetPasswordEmail] = useState();
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState();

    const checkErrors = useCallback(() => {
        let newErrors = {};
        if (!email || !isValidEmail(email)) newErrors.email = 'Please enter a valid email (max 255 characters)';
        if (!password || !isValidPassword(password)) {
            newErrors.password = 'Please enter a valid password (between 6 and 255 characters long)';
        }
        return newErrors;
    }, [email, password]);

    useEffect(() => {
        if (email === undefined || password === undefined) return;
        setErrors(checkErrors());
    }, [email, password, checkErrors]);

    const onSubmit = async (e) => {
        e.preventDefault();

        let newErrors = checkErrors();
        if (Object.keys(newErrors).length !== 0) return setErrors(newErrors);

        setLoading(true);

        try {
            const response = await API.post('/auth/login/', { email, password });
            if (response) {
                localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, response.data.token);
                window.location.href = '/';
                return;
            }
            toast.error('Invalid email or password');
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.log(error);
            if (error.response.status === 400) {
                toast.error('Invalid email or password');
                return;
            }
            toast.error('Unexpected error, please try again');
        }
    };

    const sendResetPasswordLink = async () => {
        if (!resetPasswordEmail) return;

        setLoading(true);

        try {
            const response = await API.post('/user/forgot-password', {
                email: resetPasswordEmail,
            });
            if (response.status === 200) {
                setLoading(false);
                setIsResetPasswordModalOpen(false);
                toast.success(
                    'If the account exists, you will receive an email shortly (it might be in your spams folder)!',
                    { duration: 4000 }
                );
                return;
            }
            toast.error('Could not send a link to that email');
            setIsResetPasswordModalOpen(false);
            setLoading(false);
        } catch (error) {
            toast.error('Could not send a link to that email');
            setIsResetPasswordModalOpen(false);
            setLoading(false);
        }
    };

    const onForgotPasswordClick = () => {
        setIsResetPasswordModalOpen(true);
    };

    if (loading) return <Loader className="h-screen" />;

    return (
        <div>
            <div className="w-screen flex justify-center absolute mt-4">
                <img src={logoIcon} alt="Payonix logo" width={40} />
            </div>
            <div className="flex items-center justify-center w-screen h-screen max-w-screen-2xl mx-auto">
                <form className="flex flex-col w-full px-8 md:px-0 md:w-5/12 xl:w-1/3">
                    <strong className="text-4xl mb-4">Log in</strong>
                    <span className="mb-8">
                        Don't have an account yet?{' '}
                        <Link to={'/signup'} className="text-pink">
                            Sign up
                        </Link>
                    </span>
                    <Input
                        type="email"
                        value={email}
                        setValue={setEmail}
                        label="Email"
                        fieldName="email"
                        error={errors && errors['email']}
                    />
                    <Input
                        type="password"
                        value={password}
                        setValue={setPassword}
                        label="Password"
                        fieldName="password"
                        error={errors && errors['password']}
                    />
                    <button
                        type="button"
                        className="underline text-xs text-pink text-left"
                        onClick={onForgotPasswordClick}
                    >
                        Forgot password?
                    </button>
                    <Button primary onClick={onSubmit} text="Log in" className="mt-8" />
                </form>
                <img
                    src={loginIllustration}
                    alt="log in illustration"
                    className="hidden md:block md:w-5/12 md:ml-16 xl:w-1/3 xl:ml-48 p-8"
                />
            </div>
            <div className="w-screen flex justify-center absolute bottom-0 mb-4 text-gray-darker text-xs">
                © 2022 Payonix.com. All rights reserved
            </div>
            <Modal
                title="Reset your password"
                isOpen={isResetPasswordModalOpen}
                setIsOpen={setIsResetPasswordModalOpen}
            >
                <p className="text-gray-darker mb-8">
                    A link to reset your password will be sent to the email address you enter here <br />
                    if it has an account on Payonix.
                </p>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        sendResetPasswordLink();
                    }}
                >
                    <Input
                        type="email"
                        value={resetPasswordEmail}
                        setValue={setResetPasswordEmail}
                        placeholder="Your email"
                        label="Enter the email of the account to reset the password for:"
                        fieldName="resetPasswordEmail"
                        required
                    />
                    <Button primary text="Reset password" className="mt-8 ml-auto px-4" />
                </form>
            </Modal>
        </div>
    );
};

export default Login;
